import { useEffect, useState } from 'react';
import fire from '@store/firebase';
import { IpostingItem } from '@typings/posting/posting.interface';

type Params = {
  collectionPath: string;
  currentDoc?: IpostingItem | null;
  orderBy: OrderByType; // 'createdAt' | 'date' | 'order' | 'fixedOrder'
  orderDir: OrderDirType; // 'asc' | 'desc'
  enabled?: boolean;
};

/**
 * PostingDetail 이전/다음 글 탐색용 커서 기반 쿼리 훅.
 * - 전체 컬렉션 listen 없이 진입 시 최대 2회(+순환 fallback 2회) 1회성 쿼리.
 * - link/html 글 포함 모든 문서가 쿼리 대상 → deprecated {collectionPath}IdList 의존 제거.
 * - 시리즈 모드에서는 사용하지 않음 (enabled=false).
 *
 * spec: /spec/prd/posting_prev_next_cursor_prd.md
 */
export function usePrevNextDocCursor({
  collectionPath,
  currentDoc,
  orderBy,
  orderDir,
  enabled = true,
}: Params) {
  const [prevID, setPrevID] = useState<string | null>(null);
  const [nextID, setNextID] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const currentDocId = currentDoc?.id;
  const cursorValue = currentDoc ? (currentDoc as any)[orderBy] : undefined;

  useEffect(() => {
    if (!enabled) {
      setPrevID(null);
      setNextID(null);
      return;
    }
    if (!collectionPath || !currentDocId || cursorValue === undefined) {
      return;
    }

    let cancelled = false;
    const invertedDir: OrderDirType = orderDir === 'asc' ? 'desc' : 'asc';

    const runOne = async (dir: OrderDirType): Promise<string | null> => {
      const base = fire
        .firestore()
        .collection(collectionPath)
        .orderBy(orderBy, dir);

      // startAfter 로 현재 문서 이후 1개
      const afterSnap = await base.startAfter(cursorValue).limit(1).get();
      const afterDoc = afterSnap.docs.find((d) => d.id !== currentDocId);
      if (afterDoc) return afterDoc.id;

      // 순환 fallback: 끝에 도달 → 반대 끝 첫 문서
      const wrapSnap = await base.limit(1).get();
      const wrapDoc = wrapSnap.docs.find((d) => d.id !== currentDocId);
      return wrapDoc ? wrapDoc.id : null;
    };

    setLoading(true);
    Promise.all([runOne(invertedDir), runOne(orderDir)])
      .then(([prev, next]) => {
        if (cancelled) return;
        setPrevID(prev);
        setNextID(next);
      })
      .catch((err) => {
        console.error('usePrevNextDocCursor error:', err);
        if (cancelled) return;
        setPrevID(null);
        setNextID(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, collectionPath, currentDocId, cursorValue, orderBy, orderDir]);

  return { prevID, nextID, loading };
}

export default usePrevNextDocCursor;
